import React, { useEffect } from "react"
import styled from "styled-components"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  fetchSamples,
  selectSamplesByContext,
  selectSamplesByIds,
} from "../samples/slice"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import DataPageHeader from "../../ui/DataPageHeader"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import ErrorMsg from "../../ui/ErrorMsg"
import NameIDHeader from "../../ui/NameIDHeader"
import { parseSampleFilters } from "../samples/filters"
import PropTypes from "prop-types"
import Table, { Row, Cell } from "../../ui/Table"
import TableHeaderRow from "../../ui/TableHeaderRow"
import TableHeader from "../../ui/TableHeader"
import RSOFilter from "../rsos/filters/RSOFilter"
import DateTimeFilter from "../filters/DateTimeFilter"
import {
  selectSubmissionSelectedSamples,
  addSampleToSubmission,
  removeSampleFromSubmission,
} from "./slice"
const PAGE_SIZE = 50 // Default initial and subsequent page size

const Filters = styled(Paper)`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 1rem;
`
const ObservationsBlock = styled(Paper)`
  margin-bottom: 1rem;
`

/**
 * SamplesSelection renders a page that allows you to select samples from observations and prepare them as a submission
 * to a data source such as the UDL or even an email address.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const SamplesSelection = () => {
  const dispatch = useDispatch()

  // we get any matching samples
  const samplesMatches = useSelector((state) =>
    selectSamplesByContext(state, "submission")
  )
  // We get the full objects from the ids that match
  const samples = useSelector((state) =>
    selectSamplesByIds(state, samplesMatches?.ids || [])
  )

  const selectedSamples = useSelector((state) =>
    selectSubmissionSelectedSamples(state)
  )

  const handleSelectSample = (e, sampleId) => {
    if (e.target.checked) {
      dispatch(addSampleToSubmission(sampleId))
    } else {
      dispatch(removeSampleFromSubmission(sampleId))
    }
  }

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseSampleFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)

  useEffect(() => {
    if (filter.object_id) {
      dispatch(
        fetchSamples({
          filter,
          page: { offset: 0, limit: PAGE_SIZE },
          context: ["submission"],
        })
      )
    }
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const navigate = useNavigate()
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  const samplesByObservationId = samples.reduce((group, sample) => {
    group[sample.derived_from] = group[sample.derived_from] ?? []
    group[sample.derived_from].push(sample)
    return group
  }, {})

  return (
    <Layout>
      <NavMain />
      {samples?.length === 0 && samplesMatches?._loadStatus === "loading" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      <ScrollablePage>
        <Helmet>
          <title>Data submission - Astrosite</title>
        </Helmet>

        <DataPageHeader
          title={<NameIDHeader name={"Pick data samples for submission"} />}
          subtitle={
            <>
              {samples.length > 0 && (
                <span>
                  Showing <strong>{samples?.page?.end_index}</strong>
                  {samplesMatches?.page?.total_count && (
                    <span>
                      {" "}
                      of{" "}
                      <strong className="count total_count">
                        {samplesMatches.page.total_count}
                      </strong>
                    </span>
                  )}{" "}
                  samples
                </span>
              )}
              {samplesMatches?._loadStatus === "rejected" && (
                <ErrorMsg
                  msg={"There was a problem fetching observation samples"}
                  showIcon={true}
                />
              )}
            </>
          }
        />
        <Filters>
          <RSOFilter
            filter={filter}
            setFilter={setFilter}
            textEntryDebounceMs={100}
            active={filter.object_name ? true : false}
          />
          <DateTimeFilter
            filter={filter}
            setFilter={setFilter}
            active={filter.from || filter.to ? true : false}
          />
        </Filters>
        <ObservationSamples
          samples={samples}
          selectedSamples={selectedSamples}
          handleSelectSample={handleSelectSample}
          samplesByObservationId={samplesByObservationId}
        />
        {samples?.length === 0 && samplesMatches?._loadStatus === "idle" && (
          <p>There are no samples matching your criteria.</p>
        )}
      </ScrollablePage>
    </Layout>
  )
}

const ObservationSamples = ({
  selectedSamples = [],
  handleSelectSample,
  samplesByObservationId = {},
}) => {
  return Object.keys(samplesByObservationId).map((observationId) => (
    <ObservationsBlock key={observationId}>
      <div>Observation {observationId}</div>
      <SampleTable
        samples={samplesByObservationId[observationId]}
        selectedSamples={selectedSamples}
        handleSelectSample={handleSelectSample}
      />
    </ObservationsBlock>
  ))
}

ObservationSamples.propTypes = {
  selectedSamples: PropTypes.array,
  handleSelectSample: PropTypes.func.isRequired,
  samplesByObservationId: PropTypes.object,
}

const SampleTable = ({
  samples = [],
  selectedSamples = [],
  handleSelectSample,
}) => {
  return (
    <Table>
      <TableHeaderRow>
        <TableHeader>RSO</TableHeader>
        <TableHeader>At</TableHeader>
        <TableHeader>RA</TableHeader>
        <TableHeader>Dec</TableHeader>
        <TableHeader>Selected</TableHeader>
        {/*Actions*/}
        <TableHeader />
      </TableHeaderRow>
      <tbody>
        {samples.map((sample) => (
          <Row key={sample.id} zebra>
            <Cell>{sample.object_id}</Cell>
            <Cell>{sample.at}</Cell>
            <Cell>{sample.ra}</Cell>
            <Cell>{sample.dec}</Cell>
            <Cell>
              <input
                type="checkbox"
                value={sample.id}
                checked={selectedSamples.includes(sample.id)}
                onChange={(e) => handleSelectSample(e, sample.id)}
              />
            </Cell>
          </Row>
        ))}
      </tbody>
    </Table>
  )
}

SampleTable.propTypes = {
  samples: PropTypes.array,
  selectedSamples: PropTypes.array,
  handleSelectSample: PropTypes.func.isRequired,
}

export default SamplesSelection
